import {
  cloneDeep,
  compact,
  filter,
  find,
  findIndex,
  get,
  includes,
  isEmpty,
  isFinite,
  isNil,
  memoize,
} from 'lodash';
import uniqueId from 'lodash/uniqueId';
import values from 'lodash/values';
import moment from 'moment';
import React from 'react';
import { RouterProps } from 'react-router';
import { Option } from 'react-select';

import { CtaButton } from '@opendoor/bricks';
import { getContingentOfferStatuses, getFinancingTypes } from '../../api';
import { trackEvent } from '../../api/analytics';
import { IAssigneeBasic, IDocumentInfo, IListing, INotification, Omit } from '../../declarations';
import sanitizeS3Key from '../../helpers/sanitizeS3Key';
import layoutWrapper from '../../hocs/layoutWrapper';
import Footer from '../common/Footer';
import NotificationBox from '../common/NotificationBox';
import OperatorInfoBox, { IOperatorInfoBoxProps } from '../common/OperatorInfoBox';
import { ISplitPdf } from '../common/PdfSplittingModal';
import DocumentUploadCard, { IDocumentObject } from './DocumentUploadCard';
import EditListingModal from './EditListingModal';
import ListingInfoBox, { IListingInfoBoxProps } from './ListingInfoBox';
import LowerCard from './LowerCard';
import OfferCommentForm from './OfferCommentForm';
import OfferDetailForm from './OfferDetailForm';

import './OfferDetailPageStyle.less';

export interface IOfferDetailPageSplitPdf extends ISplitPdf {
  s3Key: string;
}

export interface IOfferDetailPageProps extends RouterProps {
  deleteDocument: (fileTypeId: string | number) => void;
  documentsWithUploadErrors: Set<string | number>;
  homeAdvisorInfo: IAssigneeBasic | null;
  listingInfo: IListing;
  onListingUpdate: (listingSlug: string) => void;
  onSubmit: (offerDetails: IOfferDetailPageOnSubmitParams) => void;
  pendingDocumentUploads: boolean;
  uploadDocument: (document: { file: File; s3Key: string }, fileTypeId?: number | string) => void;
}

export interface IOfferDetailPageOnSubmitParams {
  agentComment: string;
  closeOfEscrow: any;
  contingentOfferStatus: string;
  interestedInLower: boolean;
  documentList: IDocumentObject[];
  financingType: string;
  isContingentOffer: boolean;
  offeredByType: OfferedByType;
  offerPrice: number;
  pdfSplittings: IOfferDetailPageSplitPdf[];
  sellerConcession: number;
  typedAgentEmail: string;
  typedAgentFirstName: string;
  typedAgentFullName: string;
  typedAgentLastName: string;
  typedAgentPhoneNumber: string;
  typedAgentMlsId: string;
  typedAgentMlsOfficeId: string;
  typedAgentMlsLicenseNumber: string;
}

interface IOfferDetailPageState extends Omit<IOfferDetailPageOnSubmitParams, 'pdfSplittings'> {
  contingentOfferStatusOptions: Option[];
  section: number;
  sectionHasBeenSubmittedFlags: object;
  derivedDocumentList: IDocumentObject[];
  financingTypeOptions: Option[];
  isListingModalOpen: boolean;
  offerPriceIsValid: boolean;
  savedPdfSplittings: {
    [id: string]: IOfferDetailPageSplitPdf;
  };
  typedAgentEmailIsValid: boolean;
  typedAgentPhoneNumberIsValid: boolean;
  unblockFunc: () => void;
  workingPdfId: string | null;
}

const prequalDocType = 'prequal';

// we want to make sure uniqueId stays consistent given the same market
const getAllRequiredDocuments = memoize(
  (
    market: string,
    initiatesResaleOfferWithPreContractDocument: boolean,
    preContractDocumentInfo: IDocumentInfo | null,
  ) =>
    compact([
      !initiatesResaleOfferWithPreContractDocument && {
        displayName: 'Purchase contract',
        documentType: 'purchase_agreement',
        file: null,
        fileName: null,
        required: true,
        uniqueId: `purchase_agreement-${uniqueId()}`,
      },
      initiatesResaleOfferWithPreContractDocument && {
        displayName: preContractDocumentInfo.name,
        documentType: preContractDocumentInfo.document_type,
        file: null,
        fileName: null,
        required: true,
        uniqueId: `${preContractDocumentInfo.document_type}-${uniqueId()}`,
      },
      {
        // tslint:disable-next-line:quotemark
        displayName: "Buyer's pre-qualification form",
        documentType: prequalDocType,
        file: null,
        fileName: null,
        required: true,
        uniqueId: `${prequalDocType}-${uniqueId()}`,
      },
      {
        displayName: 'Affiliated business disclosure',
        documentType: 'abad',
        file: null,
        fileName: null,
        required: false,
        uniqueId: `abad-${uniqueId()}`,
      },
      // a 'deal_sheet' is so basic that it doesn't make sense to have the OD addendum, as it's an
      // addendum to something that doesn't exist (the contract). So we do not require OD addendum
      // at the time of resale offer submission. The OD addendum is added to the transaction
      // during the Attorney Review process (before actually entering contract)
      (!preContractDocumentInfo || preContractDocumentInfo.document_type !== 'deal_sheet') && {
        displayName: 'Opendoor addendum',
        documentType: 'od_addendum',
        file: null,
        fileName: null,
        required: true,
        uniqueId: `od_addendum-${uniqueId()}`,
      },
    ]),
);

type FormFieldName =
  | 'agentComment'
  | 'closeOfEscrow'
  | 'contigentOfferStatus'
  | 'financingType'
  | 'offeredByType'
  | 'interestedInLower'
  | 'isContingentOffer'
  | 'offerPrice'
  | 'sellerConcession'
  | 'typedAgentEmail'
  | 'typedAgentFullName'
  | 'typedAgentPhoneNumber'
  | 'typedAgentMlsId'
  | 'typedAgentMlsOfficeId'
  | 'typedAgentMlsLicenseNumber';

export type OfferedByType = 'offered_by_mls_agent' | 'offered_by_non_mls_agent';

const NEXT_BUTTON_LABELS = [
  'Next: Upload Documents', // step 1
  'Next', // step 2 in the case of mortgage interest
  'Last Step: Contact Info', // step 2 in the case of no mortgage interest
  'Submit Offer', // step 3
];

export const NUM_SECTIONS = 4;

const MAYBE_MORTGAGE_SECTION = 2;
const PROGRESS_BAR_WIDTHS = [10, 55, 80];

// TODO(warehousing): Put this back the way it was when warehousing is over
// Adjusting these for 2020 Warehousing — everything is pre_listing and we want to accept offers
// const PRELIST_FLIP_STATES = ['early_access', 'in_renovation', 'pre_listing'];
const PRELIST_FLIP_STATES = ['in_renovation'];
const IN_CONTRACT_FLIP_STATES = ['resale_in_contract', 'resale_contingencies_released'];

export class OfferDetailPage extends React.Component<IOfferDetailPageProps, IOfferDetailPageState> {
  public state: IOfferDetailPageState = {
    agentComment: '',
    closeOfEscrow: null,
    contingentOfferStatus: '',
    contingentOfferStatusOptions: [] as Option[],
    // TO DO: pull dynamically from backend
    derivedDocumentList: [
      ...getAllRequiredDocuments(
        this.props.listingInfo.market,
        this.props.listingInfo.initiates_resale_offer_with_pre_contract_document,
        this.props.listingInfo.pre_contract_document_info,
      ),
    ],
    documentList: [
      ...getAllRequiredDocuments(
        this.props.listingInfo.market,
        this.props.listingInfo.initiates_resale_offer_with_pre_contract_document,
        this.props.listingInfo.pre_contract_document_info,
      ),
    ],
    financingType: '',
    financingTypeOptions: [] as Option[],
    interestedInLower: null,
    isContingentOffer: null,
    isListingModalOpen: false,
    offeredByType: 'offered_by_mls_agent',
    offerPrice: null,
    offerPriceIsValid: false,
    savedPdfSplittings: {},
    section: 0,
    sectionHasBeenSubmittedFlags: {},
    sellerConcession: 0,
    typedAgentEmail: '',
    typedAgentEmailIsValid: false,
    typedAgentFirstName: '',
    typedAgentFullName: '',
    typedAgentLastName: '',
    typedAgentMlsId: '',
    typedAgentMlsLicenseNumber: '',
    typedAgentMlsOfficeId: '',
    typedAgentPhoneNumber: '',
    typedAgentPhoneNumberIsValid: false,
    unblockFunc: null,
    workingPdfId: null,
  };

  private onAgentCommentChange = this.onInputChange.bind(this, 'agentComment');
  private onAgentEmailChange = this.onInputChange.bind(this, 'typedAgentEmail');
  private onAgentFirstNameChange = this.onInputChange.bind(this, 'typedAgentFirstName');
  private onAgentLastNameChange = this.onInputChange.bind(this, 'typedAgentLastName');
  private onAgentPhoneNumberChange = this.onInputChange.bind(this, 'typedAgentPhoneNumber');
  private onCloseOfEscrowChange = this.onInputChange.bind(this, 'closeOfEscrow');
  private onContingentOfferStatusChange = this.onInputChange.bind(this, 'contingentOfferStatus');
  private onInterestedInLowerChange = this.onInputChange.bind(this, 'interestedInLower');
  private onFinancingTypeChange = this.onInputChange.bind(this, 'financingType');
  private onIsContingencyOfferChange = this.onInputChange.bind(this, 'isContingentOffer');
  private onOfferPriceChange = this.onInputChange.bind(this, 'offerPrice');
  private onSellerConcessionChange = this.onInputChange.bind(this, 'sellerConcession');
  private onAgentMlsIdChange = this.onInputChange.bind(this, 'typedAgentMlsId');
  private onAgentMlsOfficeIdChange = this.onInputChange.bind(this, 'typedAgentMlsOfficeId');
  private onAgentMlsLicenseNumberChange = this.onInputChange.bind(
    this,
    'typedAgentMlsLicenseNumber',
  );
  private onOfferedByTypeChange = this.onInputChange.bind(this, 'offeredByType');

  public async componentDidMount() {
    const { listingInfo } = this.props;

    const isPreListing = includes(PRELIST_FLIP_STATES, listingInfo.flip_state);
    const warningText =
      'Are you sure you want to leave this page?\n\n' + 'Changes you made may not be saved.';

    window.onbeforeunload = (): boolean => {
      trackEvent('resale-offer-portal', 'click', 'close-tab', {
        pendingDocumentUploads: JSON.stringify(this.props.pendingDocumentUploads),
        state: JSON.stringify(this.state),
      });
      return confirm(warningText);
    };

    const unblockFunc = this.props.history.block((location, action) => {
      return action === 'POP' && !isPreListing ? confirm(warningText) : true;
    });

    const financingTypes = await getFinancingTypes();
    const financingTypeOptions = this.createOptions(financingTypes.data);

    const contingentOfferStatuses = await getContingentOfferStatuses();
    const contingentOfferStatusOptions = this.createOptions(contingentOfferStatuses.data);

    this.setState({
      contingentOfferStatusOptions,
      financingTypeOptions,
      unblockFunc,
    });
  }

  public findDocumentFileInPdfSplitting = (uniqueId: string): File => {
    const { savedPdfSplittings } = this.state;
    if (!savedPdfSplittings) {
      return;
    }
    for (const key of Object.keys(savedPdfSplittings)) {
      const splitPdf = savedPdfSplittings[key];
      const matchingDocument = savedPdfSplittings[key].documentSplittings.find(document => {
        return document.uniqueId === uniqueId;
      });
      if (matchingDocument) {
        return splitPdf.pdfFile;
      }
    }
  };

  public updateDocumentList = () => {
    const { documentList, savedPdfSplittings } = this.state;

    const deriveDocumentList = (
      documentList: IDocumentObject[],
      pdfSplittings: { [id: string]: IOfferDetailPageSplitPdf },
    ) => {
      const updatedDocuments = documentList.map(document => {
        const updatedDocumentFile = this.findDocumentFileInPdfSplitting(document.uniqueId);
        if (updatedDocumentFile) {
          return {
            ...document,
            file: updatedDocumentFile,
            fileName: updatedDocumentFile.name,
          };
        }
        return document;
      });

      for (const key of Object.keys(pdfSplittings)) {
        const splitPdf = pdfSplittings[key];
        for (const document of pdfSplittings[key].documentSplittings) {
          if (!updatedDocuments.some(doc => doc.displayName === document.displayName)) {
            const newDocument = {
              displayName: document.displayName,
              documentType: 'misc',
              file: splitPdf.pdfFile,
              fileName: splitPdf.pdfFile.name,
              required: false,
              uniqueId: document.uniqueId || uniqueId('split_pdf_'),
            };
            updatedDocuments.push(newDocument);
          }
        }
      }
      return updatedDocuments;
    };

    const updatedDocuments = deriveDocumentList(documentList, savedPdfSplittings);
    this.setState({ derivedDocumentList: updatedDocuments });
  };

  public componentWillUnmount() {
    window.onbeforeunload = null;
    this.state.unblockFunc();
  }

  public render(): JSX.Element {
    const { listingInfo, onListingUpdate, pendingDocumentUploads } = this.props;

    const { section, isListingModalOpen } = this.state;
    return includes(PRELIST_FLIP_STATES, listingInfo.flip_state) ? (
      this.renderPreListing()
    ) : (
      <div className="od-offer-detail-page">
        <div className="inner-container od-offer-detail-body">
          <h5 className="tag-line">Buy with Opendoor</h5>
          <div className="row">
            <div className="col-xs-4">
              <OperatorInfoBox {...this.mapPropsToHomeAdvisorInfoBox()} />
              <div className="divider-left" />
              <ListingInfoBox {...this.mapPropsToListingInfoBoxProps()} />
              <a className="edit-address-link" onClick={this.onClickEditListing}>
                Edit address
              </a>
              <EditListingModal
                isOpen={isListingModalOpen}
                onRequestClose={this.onListingModalClose}
                onSubmit={onListingUpdate}
              />
              {listingInfo.listing_packet_redirect_url && (
                <div>
                  <div className="divider-left" />
                  <div className="download-documents-link">
                    <a href={listingInfo.listing_packet_redirect_url} target="_blank">
                      Download required documents
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="col-xs-8">
              <p>
                Thank you for your interest in {listingInfo.address.street}.<br />
                Please contact the Opendoor Agent regarding any questions about this property.
              </p>
              {this.renderNotifications()}
              {!listingInfo.in_doorsey_pilot_treatment_group && (
                <section className="body-section">{this.renderCurrentSection()}</section>
              )}
            </div>
          </div>
          <Footer />
        </div>
        {!listingInfo.in_doorsey_pilot_treatment_group && (
          <div className="offer-detail-footer">
            <div className="offer-detail-progress-bar">
              <div className="odpb-inner" style={{ width: `${PROGRESS_BAR_WIDTHS[section]}%` }} />
            </div>
            <div className="inner-container">
              <div className="row">
                <div className="col-xs-12 od-back-next-buttons">
                  <div className="od-footer-button od-footer-button-next">
                    <CtaButton
                      actionText={NEXT_BUTTON_LABELS[section]}
                      ctaStyle="primary"
                      loading={pendingDocumentUploads}
                      onClick={this.onGoNextSection}
                    />
                  </div>
                  {section > 0 && (
                    <div className="od-footer-button od-footer-button-back">
                      <a href="" onClick={this.onGoBackSection}>
                        Back
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  public renderPreListing = () => {
    const { listingInfo, onListingUpdate } = this.props;
    const { isListingModalOpen } = this.state;
    return (
      <div className="od-offer-detail-page">
        <div className="inner-container od-offer-detail-body">
          <h5 className="tag-line">Buy with Opendoor</h5>
          <div className="row">
            <div className="col-xs-4">
              <div className="divider-left" />
              <ListingInfoBox {...this.mapPropsToListingInfoBoxProps()} />
              <a className="edit-address-link" onClick={this.onClickEditListing}>
                Edit address
              </a>
              <EditListingModal
                isOpen={isListingModalOpen}
                onRequestClose={this.onListingModalClose}
                onSubmit={onListingUpdate}
              />
              {listingInfo.listing_packet_redirect_url && (
                <div>
                  <div className="divider-left" />
                  <div className="download-documents-link">
                    <a href={listingInfo.listing_packet_redirect_url} target="_blank">
                      Download required documents
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="col-xs-8">
              <p>Thank you for your interest in {listingInfo.address.street}.</p>
              {this.renderNotifications()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  private onGoBackSection = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const previousSection = this.state.section - 1;
    if (previousSection === MAYBE_MORTGAGE_SECTION && !this.canShowMortgagePage()) {
      this.setState({ section: previousSection - 1 });
    } else if (previousSection >= 0) {
      this.setState({ section: previousSection });
    }
  };

  private onGoNextSection = () => {
    if (!this.checkCanSubmit()) {
      return;
    }
    const nextSection = this.state.section + 1;
    // if we cannot show mortgage page or financing type is incorrect move onto next section
    if (nextSection === MAYBE_MORTGAGE_SECTION && !this.canShowMortgagePage()) {
      this.setState({ section: nextSection + 1 });
    } else if (nextSection < NUM_SECTIONS) {
      this.setState({ section: nextSection });
    } else if (nextSection === NUM_SECTIONS) {
      this.onSubmitHandler();
    }
  };

  private fhaNotification(marketListingsUrl: string, fhaEligibleDate: string): INotification {
    return {
      content: (
        <span>
          To view a list of homes eligible for FHA offers, please click{' '}
          <a target="_blank" href={marketListingsUrl}>
            here.
          </a>
        </span>
      ),
      title: `This home is not eligible to receive FHA offers until ${moment(
        fhaEligibleDate,
      ).format('LL')}`,
    };
  }

  private activeOfferNotification(detailText: string): INotification {
    return {
      content: detailText,
      title: 'The seller is considering offers',
    };
  }

  private doorseyNotification(detailText: string): INotification {
    return {
      content: detailText,
      title: 'Please visit www.doorsey.com to submit an offer',
    };
  }

  private homeInRenoNotification(): INotification {
    const { listingInfo } = this.props;
    const contactEmail = listingInfo.address.market.buyer_contact_email;
    return {
      content: (
        <div>
          To submit an offer on this home, please email{' '}
          <a href={`mailto:${contactEmail}`} target="_blank">
            {contactEmail}
          </a>
        </div>
      ),
      title: 'This home is in renovation',
    };
  }

  // TODO(warehousing): Put this back the way it was when warehousing is over
  // private homePrelistingNotification(): INotification {
  //   const { listingInfo } = this.props;
  //   const contactEmail = listingInfo.address.market.buyer_contact_email;
  //   return {
  //     content: (
  //       <div>
  //         To submit an offer on this home, please email{' '}
  //         <a href={`mailto:${contactEmail}`} target="_blank">
  //           {contactEmail}
  //         </a>
  //       </div>
  //     ),
  //     title: 'This home has not been listed',
  //   };
  // }

  private noActiveOfferNotification(detailText: string): INotification {
    return {
      content: detailText,
      title: 'Make the first offer on this home',
    };
  }

  private shouldShowLicenseNumberInput = () => {
    const { offeredByType } = this.state;
    const { listingInfo } = this.props;
    return offeredByType === 'offered_by_non_mls_agent' && listingInfo.market === 'atlanta';
  };

  private shouldShowOfficeIdInput = () => {
    const { offeredByType } = this.state;
    const { listingInfo } = this.props;
    return offeredByType === 'offered_by_mls_agent' && listingInfo.market === 'nashville';
  };

  private shouldShowMlsIdInput = () => {
    const { offeredByType } = this.state;
    return offeredByType === 'offered_by_mls_agent';
  };

  private inContractNotification(homeAdvisorEmail: string): INotification {
    return {
      content: (
        <span>
          Feel free to submit a backup offer or email the listing agent directly at{' '}
          <a href={`mailto:${homeAdvisorEmail}`}>{homeAdvisorEmail}</a>
        </span>
      ),
      title: 'This home is currently in contract',
    };
  }

  private canShowMortgagePage = () => {
    return this.isValidMortgageFinancingType(this.state.financingType);
  };

  private isValidMortgageFinancingType = (financingType: string) => {
    return financingType !== 'cash';
  };

  private renderMortgageCard = () => {
    const { interestedInLower, section, sectionHasBeenSubmittedFlags } = this.state;

    return (
      <LowerCard
        interestedInLower={interestedInLower}
        hasBeenSubmitted={sectionHasBeenSubmittedFlags[section]}
        onInterestedInLowerChange={this.onInterestedInLowerChange}
      />
    );
  };

  private renderCurrentSection() {
    const { documentsWithUploadErrors, listingInfo } = this.props;

    const {
      agentComment,
      closeOfEscrow,
      contingentOfferStatus,
      contingentOfferStatusOptions,
      section,
      derivedDocumentList,
      financingType,
      financingTypeOptions,
      isContingentOffer,
      offeredByType,
      offerPrice,
      savedPdfSplittings,
      sellerConcession,
      typedAgentEmail,
      typedAgentFirstName,
      typedAgentLastName,
      typedAgentPhoneNumber,
      typedAgentMlsId,
      typedAgentMlsOfficeId,
      typedAgentMlsLicenseNumber,
      workingPdfId,
      sectionHasBeenSubmittedFlags,
    } = this.state;

    switch (section) {
      case 0:
        return (
          <OfferDetailForm
            closeOfEscrow={closeOfEscrow}
            contingentOfferStatus={contingentOfferStatus}
            contingentOfferStatusOptions={contingentOfferStatusOptions}
            financingType={financingType}
            financingTypeOptions={financingTypeOptions}
            isContingentOffer={isContingentOffer}
            listingInfo={listingInfo}
            maxAllowedOfferPriceCents={listingInfo.max_allowed_offer_price_cents}
            offerPrice={offerPrice}
            onCloseOfEscrowChange={this.onCloseOfEscrowChange}
            onContingentOfferStatusChange={this.onContingentOfferStatusChange}
            onIsContingencyOfferChange={this.onIsContingencyOfferChange}
            onOfferPriceChange={this.onOfferPriceChange}
            onSellerConcessionChange={this.onSellerConcessionChange}
            onFinancingTypeChange={this.onFinancingTypeChange}
            sellerConcession={sellerConcession}
            hasBeenSubmitted={sectionHasBeenSubmittedFlags[section]}
          />
        );
      case 1:
        return (
          <DocumentUploadCard
            documents={derivedDocumentList}
            documentsWithUploadErrors={documentsWithUploadErrors}
            listingPacketUrl={listingInfo.listing_packet_redirect_url}
            listingSlug={listingInfo.slug}
            onAddFileUploadElement={this.onAddFileUpload}
            onClosePdfSplittingModal={this.onCancelPdfSplitting}
            onDeleteFile={this.onDeleteFile}
            onDeletePdf={this.onDeletePdf}
            onDropAccepted={this.onDropAccepted}
            onEditPages={this.onEditPages}
            onSubmitDocumentToSplit={this.onSubmitDocumentToSplit}
            pdfSplittings={savedPdfSplittings}
            updateDocuments={this.updateDocumentList}
            workingPdf={workingPdfId !== null ? savedPdfSplittings[workingPdfId] : null}
            hasBeenSubmitted={sectionHasBeenSubmittedFlags[section]}
          />
        );
      case 2:
        return this.renderMortgageCard();
      case 3:
        return (
          <OfferCommentForm
            agentComment={agentComment}
            agentFirstName={typedAgentFirstName}
            agentLastName={typedAgentLastName}
            agentPhoneNumber={typedAgentPhoneNumber}
            agentEmail={typedAgentEmail}
            agentMlsId={typedAgentMlsId}
            agentMlsOfficeId={typedAgentMlsOfficeId}
            agentMlsLicenseNumber={typedAgentMlsLicenseNumber}
            offeredByType={offeredByType}
            onAgentCommentChange={this.onAgentCommentChange}
            onAgentEmailChange={this.onAgentEmailChange}
            onAgentFirstNameChange={this.onAgentFirstNameChange}
            onAgentLastNameChange={this.onAgentLastNameChange}
            onAgentPhoneNumberChange={this.onAgentPhoneNumberChange}
            onAgentMlsIdChange={this.onAgentMlsIdChange}
            onAgentMlsOfficeIdChange={this.onAgentMlsOfficeIdChange}
            onAgentMlsLicenseNumberChange={this.onAgentMlsLicenseNumberChange}
            onOfferedByTypeChange={this.onOfferedByTypeChange}
            shouldShowLicenseNumberInput={this.shouldShowLicenseNumberInput}
            shouldShowOfficeIdInput={this.shouldShowOfficeIdInput}
            shouldShowMlsIdInput={this.shouldShowMlsIdInput}
            hasBeenSubmitted={sectionHasBeenSubmittedFlags[section]}
          />
        );
    }
  }

  private renderNotifications = () => {
    const { listingInfo } = this.props;

    const fhaEligibleDate = listingInfo.fha_eligible_date;
    const homeAdvisorEmail = listingInfo.address.market.buyer_contact_email;
    const activeOfferCheckText = listingInfo.active_offer_check_text;

    const marketIdentifier = listingInfo.address.market.homes_path;
    const marketListingsUrl = `https://www.opendoor.com/homes/${marketIdentifier}?fha_eligible`;
    const flipState = listingInfo.flip_state;
    // TODO(warehousing): Put this back the way it was when warehousing is over
    // Adjusting these for 2020 Warehousing — everything is pre_listing and we want to accept offers
    const FLIP_STATE_TO_NOTIFICATION = {
      // early_access: this.homePrelistingNotification(),
      in_renovation: this.homeInRenoNotification(),
      // pre_listing: this.homePrelistingNotification(),
      resale_contingencies_released: this.inContractNotification(homeAdvisorEmail),
      resale_in_contract: this.inContractNotification(homeAdvisorEmail),
    };

    const notificationList = [] as INotification[];
    if (includes(Object.keys(FLIP_STATE_TO_NOTIFICATION), flipState)) {
      notificationList.push(FLIP_STATE_TO_NOTIFICATION[flipState]);
    }
    if (!includes(IN_CONTRACT_FLIP_STATES, flipState)) {
      // these notifications are not relevant if we're already in contract
      if (listingInfo.in_doorsey_pilot_treatment_group) {
        notificationList.push(this.doorseyNotification(activeOfferCheckText));
      } else if (listingInfo.active_offer) {
        notificationList.push(this.activeOfferNotification(activeOfferCheckText));
      } else {
        notificationList.push(this.noActiveOfferNotification(activeOfferCheckText));
      }
    }

    if (!this.isFhaEligible()) {
      notificationList.push(this.fhaNotification(marketListingsUrl, fhaEligibleDate));
    }

    return (
      notificationList.length > 0 && (
        <section className="notification-section">
          <NotificationBox notifications={notificationList} />
        </section>
      )
    );
  };

  private createDocumentList = (financingType: string): IDocumentObject[] => {
    const {
      market,
      initiates_resale_offer_with_pre_contract_document,
      pre_contract_document_info,
    } = this.props.listingInfo;
    const { documentList } = this.state;

    const hasPrequal = find(documentList, { documentType: prequalDocType });

    if (financingType === 'cash' && hasPrequal) {
      return filter(documentList, doc => doc.documentType !== prequalDocType);
    }

    const prequalIdx = findIndex(
      getAllRequiredDocuments(
        market,
        initiates_resale_offer_with_pre_contract_document,
        pre_contract_document_info,
      ),
      {
        documentType: prequalDocType,
      },
    );
    return !hasPrequal
      ? [
          ...documentList.slice(0, 1),
          getAllRequiredDocuments(
            market,
            initiates_resale_offer_with_pre_contract_document,
            pre_contract_document_info,
          )[prequalIdx],
          ...documentList.slice(1),
        ]
      : documentList;
  };

  private mapPropsToHomeAdvisorInfoBox = (): IOperatorInfoBoxProps => {
    const { homeAdvisorInfo, listingInfo } = this.props;
    const fullName = '';
    const operatorRole = '';
    let phoneNumber = listingInfo.address.market.buyer_contact_phone_number;

    if (homeAdvisorInfo) {
      phoneNumber = homeAdvisorInfo.phone_number;
    }

    return {
      brokerage: listingInfo.address.brokerage_region.brokerage_name,
      email: listingInfo.address.market.buyer_contact_email,
      fullName,
      operatorRole,
      phoneNumber,
      photoUrl: 'https://images.opendoor.com/source/s3/imgdrop-production/4cb1219c938a43169ae5ffa6237b856a.jpg?service=consumer',
    };
  };

  private mapPropsToListingInfoBoxProps = (): IListingInfoBoxProps => {
    const { listingInfo } = this.props;
    return {
      imgSrc: get(listingInfo, 'listing_photos[0].thumbnail_urls._760x480', null),
      listingStatus: listingInfo.display_state,
      livingArea: listingInfo.square_footage,
      lotSize: listingInfo.lot_square_footage,
      numBaths: listingInfo.bathrooms,
      numBeds: listingInfo.bedrooms,
      readableAddress: listingInfo.address.street,
    };
  };

  /***** HANDLERS *****/

  private onListingModalClose = () => {
    this.setState({ isListingModalOpen: false });
  };

  private onClickEditListing = () => {
    this.setState({ isListingModalOpen: true });
  };

  private onAddFileUpload = (newFile: IDocumentObject) => {
    const currDocumentList = this.state.documentList;
    this.setState({ documentList: [...currDocumentList, newFile] }, this.updateDocumentList);
  };

  private onDropAccepted = (file: File, uniqueId: string) => {
    const fileName = file.name;
    const s3Key = this.generateS3Key(file);
    const documentList = this.state.documentList.map(doc => {
      if (doc.uniqueId === uniqueId) {
        const documentType = doc.documentType || 'misc';
        trackEvent('resale-offer-portal', 'upload', documentType, {
          listing_slug: this.props.listingInfo.slug,
        });
        return { ...doc, file, s3Key, fileName };
      }
      return doc;
    });
    this.props.uploadDocument({ file, s3Key }, uniqueId);
    this.setState({ documentList }, this.updateDocumentList);
  };

  private onDeleteFile = (uniqueId: string) => {
    const { deleteDocument } = this.props;
    const documentList = this.state.documentList.reduce((prev, doc) => {
      if (doc.uniqueId === uniqueId) {
        if (doc.required) {
          prev.push({
            ...doc,
            file: null,
            fileName: null,
          });
        }
      } else {
        prev.push(doc);
      }

      return prev;
    }, []);
    deleteDocument(uniqueId);
    this.setState({ documentList }, this.updateDocumentList);
  };

  private onDeletePdf = (uniqueId: string) => {
    const { deleteDocument } = this.props;
    const { savedPdfSplittings } = this.state;
    const updatedPdfSplittings = cloneDeep(savedPdfSplittings);
    delete updatedPdfSplittings[uniqueId];
    deleteDocument(uniqueId);
    this.setState({ savedPdfSplittings: updatedPdfSplittings }, this.updateDocumentList);
  };

  private onCancelPdfSplitting = () => {
    this.setState({ workingPdfId: null });
  };

  private onEditPages = (file: File) => {
    const { savedPdfSplittings } = this.state;

    const findIdMatchingFile = (file: File): string => {
      for (const key of Object.keys(savedPdfSplittings)) {
        if (savedPdfSplittings[key].pdfFile === file) {
          return key;
        }
      }
    };

    const workingPdfId = findIdMatchingFile(file);
    if (workingPdfId) {
      this.setState({ workingPdfId });
    }
  };

  private onSubmitHandler = () => {
    if (!this.checkCanSubmit()) {
      return;
    }

    const { listingInfo, onSubmit } = this.props;
    const {
      agentComment,
      closeOfEscrow,
      contingentOfferStatus,
      documentList,
      financingType,
      interestedInLower,
      isContingentOffer,
      offeredByType,
      offerPrice,
      savedPdfSplittings,
      sellerConcession,
      typedAgentEmail,
      typedAgentFullName,
      typedAgentFirstName,
      typedAgentLastName,
      typedAgentPhoneNumber,
      typedAgentMlsId,
      typedAgentMlsLicenseNumber,
      typedAgentMlsOfficeId,
    } = this.state;

    const submitParams: IOfferDetailPageOnSubmitParams = {
      agentComment,
      closeOfEscrow,
      contingentOfferStatus,
      documentList,
      financingType,
      interestedInLower,
      isContingentOffer,
      offeredByType,
      offerPrice,
      pdfSplittings: values(savedPdfSplittings),
      sellerConcession,
      typedAgentEmail,
      typedAgentFirstName,
      typedAgentFullName,
      typedAgentLastName,
      typedAgentMlsId,
      typedAgentMlsLicenseNumber,
      typedAgentMlsOfficeId,
      typedAgentPhoneNumber,
    };

    onSubmit(submitParams);
    trackEvent('resale-offer-portal', 'click', 'submit-offer', {
      agent_full_name: typedAgentFullName,
      agent_phone_number: typedAgentPhoneNumber,
      listing_slug: listingInfo.slug,
      pendingDocumentUploads: this.props.pendingDocumentUploads,
      state: this.state,
    });
  };

  private onSubmitDocumentToSplit = (splitPdf: ISplitPdf) => {
    const { listingInfo } = this.props;
    const { savedPdfSplittings, workingPdfId } = this.state;

    const newPdfSplittings = {
      ...savedPdfSplittings,
    };

    const priorSplitPdf = newPdfSplittings[workingPdfId];
    const mergedSplitPdf = { ...priorSplitPdf, ...splitPdf };
    const newPdfId = workingPdfId || uniqueId('split_pdf_');
    if (!priorSplitPdf) {
      const s3Key = this.generateS3Key(splitPdf.pdfFile);
      trackEvent('resale-offer-portal', 'upload', '1-pdf', {
        listing_slug: listingInfo.slug,
      });
      this.props.uploadDocument({ file: splitPdf.pdfFile, s3Key }, newPdfId);
      mergedSplitPdf.s3Key = s3Key;
    }

    newPdfSplittings[newPdfId] = mergedSplitPdf;

    const deleteEmptySplittings = (pdfSplittings: { [id: string]: IOfferDetailPageSplitPdf }) => {
      for (const id of Object.keys(pdfSplittings)) {
        const documentSplittings = pdfSplittings[id].documentSplittings;
        if (documentSplittings.length === 0) {
          delete pdfSplittings[id];
        }
      }
    };

    deleteEmptySplittings(newPdfSplittings);

    for (const id of Object.keys(newPdfSplittings)) {
      newPdfSplittings[id].documentSplittings.forEach(document => {
        if (document.uniqueId === undefined) {
          document.uniqueId = uniqueId('split_pdf_');
        }
      });
    }

    const addUniqueIds = (pdfSplittings: { [id: string]: IOfferDetailPageSplitPdf }) => {
      for (const id of Object.keys(pdfSplittings)) {
        pdfSplittings[id].documentSplittings.forEach(document => {
          document.uniqueId = document.uniqueId || uniqueId('split_pdf_');
        });
      }
    };

    addUniqueIds(newPdfSplittings);

    this.setState(
      {
        savedPdfSplittings: newPdfSplittings,
        workingPdfId: null,
      },
      this.updateDocumentList,
    );
  };

  private checkCanSubmit = () => {
    const {
      closeOfEscrow,
      contingentOfferStatus,
      derivedDocumentList,
      financingType,
      interestedInLower,
      isContingentOffer,
      offerPriceIsValid,
      section,
      sectionHasBeenSubmittedFlags,
      sellerConcession,
      typedAgentEmail,
      typedAgentEmailIsValid,
      typedAgentFirstName,
      typedAgentLastName,
      typedAgentMlsLicenseNumber,
      typedAgentMlsOfficeId,
      typedAgentMlsId,
      typedAgentPhoneNumber,
      typedAgentPhoneNumberIsValid,
    } = this.state;
    const { documentsWithUploadErrors, pendingDocumentUploads } = this.props;

    this.setState({
      sectionHasBeenSubmittedFlags: {
        ...sectionHasBeenSubmittedFlags,
        [section]: true,
      },
    });

    switch (section) {
      case 0: // Step 1 (key offer information)
        return (
          offerPriceIsValid &&
          isFinite(sellerConcession) &&
          !isEmpty(closeOfEscrow) &&
          !isEmpty(financingType) &&
          !isNil(isContingentOffer)
        );
      case 1: // Step 2 (upload documents)
        return (
          !pendingDocumentUploads &&
          isEmpty(documentsWithUploadErrors) &&
          derivedDocumentList.every(doc => !doc.required || !isNil(doc.file))
        );
      case 2:
        return !isNil(interestedInLower);
      case 3: // Step 3 (agent name/email/phone)
        // helper to validate that fields are present, but we can't fail validation
        // if a user shouldn't fill in that field
        const validateShownField = (showCondition: () => boolean, field: string | null) => {
          // hidden fields are valid
          if (!showCondition()) {
            return true;
          }
          return field;
        };
        return (
          (!isContingentOffer || !isEmpty(contingentOfferStatus)) &&
          typedAgentFirstName &&
          typedAgentLastName &&
          typedAgentEmail &&
          typedAgentEmailIsValid &&
          typedAgentPhoneNumber &&
          typedAgentPhoneNumberIsValid &&
          validateShownField(this.shouldShowLicenseNumberInput, typedAgentMlsLicenseNumber) &&
          validateShownField(this.shouldShowMlsIdInput, typedAgentMlsId) &&
          validateShownField(this.shouldShowOfficeIdInput, typedAgentMlsOfficeId)
        );
    }
    return false;
  };

  private onInputChange(
    fieldName: FormFieldName,
    newValue: string | number | boolean,
    isValid?: boolean,
  ) {
    const newState = {
      [fieldName]: newValue,
    } as Partial<IOfferDetailPageState>;

    if (
      fieldName === 'financingType' &&
      (this.state.financingType === 'cash' || newValue === 'cash')
    ) {
      newState['documentList'] = this.createDocumentList(newValue as string);
      newState['derivedDocumentList'] = this.createDocumentList(newValue as string);
    }

    // if validations exist
    if (isValid != null) {
      newState[`${fieldName}IsValid`] = isValid;
    }

    this.setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  }

  /***** HELPERS *****/

  private isFhaEligible = (): boolean =>
    moment(this.props.listingInfo.fha_eligible_date).diff(moment()) <= 0;

  private createOptions = (options: Option[]): Option[] => {
    return options.map(
      option =>
        option.value === 'fha_loan' && !this.isFhaEligible()
          ? { ...option, disabled: true }
          : option,
    );
  };

  private generateS3Key(file: File): string {
    const prefix = 'uploads/ResaleOfferPortal/';
    const s3KeyComponents = [this.props.listingInfo.slug, `${file.name.split('.')[0]}-${Date()}`];
    const sanitizedComponents = s3KeyComponents.map(key => sanitizeS3Key(key));
    return `${prefix}${sanitizedComponents.join('/')}.${file.name.split('.')[1]}`;
  }
}

export default layoutWrapper<IOfferDetailPageProps>(OfferDetailPage, {
  footerType: 'NONE',
  headerType: 'MINIMAL',
});
